import { useCallback, useRef, useState } from "react";
import { api, UploadCancelledError } from "../api";

export interface UploadJob {
  id: number;
  label: string;
  /** null = noch kein Progress-Event vom Browser */
  progress: number | null;
  /** true = auch nach Wartezeit kein Event → animierte Anzeige */
  stalled: boolean;
  /** Geschätzte Restzeit in Sekunden */
  eta: number | null;
}

let uploadCounter = 0;

export function useUploads(
  code: string,
  onError: (err: unknown) => void,
  onCancelled: () => void,
) {
  const [uploads, setUploads] = useState<UploadJob[]>([]);
  const controllers = useRef(new Map<number, AbortController>());

  const uploadFiles = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return;
      // Kein crypto.randomUUID: das gibt es nur in sicheren Kontexten
      const id = ++uploadCounter;
      const label =
        files.length === 1 && files[0]
          ? files[0].name
          : `${files.length} Dateien`;
      const controller = new AbortController();
      controllers.current.set(id, controller);
      setUploads((current) => [
        ...current,
        { id, label, progress: null, stalled: false, eta: null },
      ]);
      // Erst nach kurzer Wartezeit auf die animierte Anzeige umschalten –
      // ein sofortiger Start der Animation sah wie ein Springen aus
      setTimeout(() => {
        setUploads((current) =>
          current.map((job) =>
            job.id === id && job.progress === null
              ? { ...job, stalled: true }
              : job,
          ),
        );
      }, 800);
      const startedAt = Date.now();
      try {
        await api.uploadFiles(code, files, {
          signal: controller.signal,
          onProgress: ({ fraction, loaded, total }) => {
            // Restzeit aus der bisherigen Durchschnittsgeschwindigkeit
            const elapsed = (Date.now() - startedAt) / 1000;
            const eta =
              fraction !== null && fraction < 1 && loaded > 0 && elapsed > 0.5
                ? ((total - loaded) * elapsed) / loaded
                : null;
            setUploads((current) =>
              current.map((job) =>
                job.id === id
                  ? { ...job, progress: fraction ?? job.progress, eta }
                  : job,
              ),
            );
          },
        });
      } catch (err) {
        if (err instanceof UploadCancelledError) onCancelled();
        else onError(err);
      } finally {
        controllers.current.delete(id);
        setUploads((current) => current.filter((job) => job.id !== id));
      }
    },
    [code, onError, onCancelled],
  );

  const cancelUpload = useCallback((id: number) => {
    controllers.current.get(id)?.abort();
  }, []);

  return { uploads, uploadFiles, cancelUpload };
}
